import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { isLoggedIn, handleSsoCallback, redirectToSsoLogin } from 'common/utils/auth';
import { userStore } from 'store/userStore';
import { getUserInfo } from 'common/utils/secureStorage';
import { useAtomValue } from 'jotai';

interface ProtectedRouteProps {
    children: React.ReactNode;
}

/**
 * 인증이 필요한 라우트를 보호하는 컴포넌트
 * - 인증되지 않은 사용자는 SSO 로그인 페이지로 리다이렉트
 * - 로그인 후 원래 페이지로 돌아옴
 */
export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const location = useLocation();

    // SSO 회원정보 조회 상태
    const ssoUserState = useAtomValue(userStore.ssoUser.stateAtom);

    useEffect(() => {
        // SSO 콜백 처리 (토큰이 URL에 있는 경우)
        const callbackHandled = handleSsoCallback();

        if (callbackHandled) {
            setIsAuthenticated(true);
            // SSO 콜백 후 회원정보 조회
            userStore.onGetSsoUser();
            setIsLoading(false);
            return;
        }

        // 기존 토큰 확인
        const loggedIn = isLoggedIn();

        if (!loggedIn) {
            // 인증되지 않은 경우 SSO 로그인 페이지로 리다이렉트
            const returnUrl = window.location.origin + location.pathname + location.search;
            redirectToSsoLogin(returnUrl);
            return;
        }

        setIsAuthenticated(true);

        // 로그인 상태이고 저장된 사용자 정보가 없으면 조회
        if (!getUserInfo()) {
            userStore.onGetSsoUser();
        }

        setIsLoading(false);
    }, [location.pathname, location.search]);

    // SSO 회원정보 조회 결과 처리
    useEffect(() => {
        if (ssoUserState.status === 'ready' && ssoUserState.data) {
            userStore.onSaveUserInfo(ssoUserState.data);
            userStore.ssoUser.onDefault();
        } else if (ssoUserState.status === 'error') {
            console.error('Failed to fetch user info:', ssoUserState.error);
            userStore.ssoUser.onDefault();
        }
    }, [ssoUserState.status, ssoUserState.data]);

    // 로딩 중
    if (isLoading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: 'var(--offWhite, #f5f5f5)'
            }}>
                <span style={{ color: 'var(--gray500, #666)' }}>로딩 중...</span>
            </div>
        );
    }

    // 인증되지 않은 경우 (리다이렉트 중)
    if (!isAuthenticated) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: 'var(--offWhite, #f5f5f5)'
            }}>
                <span style={{ color: 'var(--gray500, #666)' }}>로그인 페이지로 이동 중...</span>
            </div>
        );
    }

    return <>{children}</>;
};
